import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex flex-row items-center justify-between mb-3 leading-tight"
}
const _hoisted_2 = ["for"]
const _hoisted_3 = {
  key: 0,
  class: "text-gray-500 font-medium text-xs"
}
const _hoisted_4 = { class: "flex justify-end gap-2 p-2" }

import { computed, onBeforeUnmount, onMounted, PropType, ref, useId, watch } from 'vue';
import { i18n, supportedLocales } from '@/i18n/i18n';
import { DatePicker } from 'v-calendar';
import { DateRangeInput } from '@/lib/components/Input';
import { BaseButton } from '@/lib/components/Button';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import { localisedMask as getLocalisedMask } from '@/helpers/date.helper';

export interface IMasks {
  input: string;
}

const rangeSeparator = ' ~ ';


export default /*@__PURE__*/_defineComponent({
  __name: 'BaseDateRangePicker',
  props: {
  modelValue: {
    type: Object as PropType<{ start: string; end: string }>,
    default: () => ({ start: '', end: '' })
  },
  mode: {
    type: String as PropType<'date' | 'dateTime'>,
    default: 'date',
    validator: (value: string) => ['date', 'dateTime'].includes(value)
  },
  isOptional: {
    type: Boolean,
    default: false
  },
  label: {
    type: String,
    default: null
  },
  iconPosition: {
    type: String,
    default: 'right'
  },
  iconColor: {
    type: String,
    default: 'black'
  },
  minDate: {
    type: String,
    default: null
  },
  maxDate: {
    type: String,
    default: null
  },
  error: {
    type: String,
    default: null
  },
  columns: {
    type: Number,
    default: 2
  },
  description: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  clear: {
    type: Boolean,
    default: false
  },
  masks: {
    type: Object as PropType<IMasks>,
    default: null
  },
  locale: {
    type: String,
    default: null
  }
},
  emits: ["keyup", "update:modelValue", "change"],
  setup(__props, { emit: __emit }) {

dayjs.extend(customParseFormat);
dayjs.extend(utc);

const props = __props;

const emit = __emit;

const datePicker = ref(null);
const id = useId();

const emittedFormat = computed(() => {
  if (props.mode === 'dateTime') {
    return 'YYYY-MM-DD HH:mm';
  }
  return 'YYYY-MM-DD';
});

const is24hr = computed(() => {
  const supportedLocale = supportedLocales.find(
    (localeObj) => localeObj.code === localeToValidate.value
  );
  return supportedLocale?.is24hr ?? true;
});

const minimumDate = computed(() => props.minDate ? dayjs(props.minDate).format() : '');
const maximumDate = computed(() => props.maxDate ? dayjs(props.maxDate).format() : '');

const pendingValue = ref<{ start: string; end: string }>(
  props.modelValue ? { ...props.modelValue } : { start: '', end: '' }
);
const confirmedValue = ref(props.modelValue);

watch(
  () => props.modelValue,
  (val) => {
    confirmedValue.value = val;
    pendingValue.value = val ? { ...val } : { start: '', end: '' };
  },
  { immediate: true, deep: true }
);

const date = computed({
  get() {
    return pendingValue.value;
  },
  set(val) {
    pendingValue.value = val;
  }
});

const getLocalisedString = (val: { start: string; end: string }) => {
  if (!val) {
    return '';
  }

  const start = val.start
    ? dayjs(val.start, emittedFormat.value).local().format(localisedMask.value)
    : '';
  const end = val.end
    ? dayjs(val.end, emittedFormat.value).local().format(localisedMask.value)
    : '';

  return start || end ? `${start}${rangeSeparator}${end}` : '';
};

const inputDisplayValue = computed(() =>
  confirmedValue.value ? getLocalisedString(confirmedValue.value) : ''
);

const localisedMask = computed(() => {
  const defaultInputMask = 'DD/MM/YYYY';
  if (props.masks) {
    return props.masks.input ? props.masks.input : defaultInputMask;
  }
  return getLocalisedMask(localeToValidate.value, props.mode);
});

const popoverOptions = computed(() => ({
  closeOnSelect: false,
  autoHide: false,
  keepVisibleOnInput: true,
  visibility: 'visible'
}));

const localeToValidate = computed<string>(() => props.locale || i18n.global.locale.value);
const datePickerRef = computed(() => datePicker.value);
const datePickerMasks = computed(() => ({
  masks: {
    input: localisedMask.value
  }
}));

const setDate = (value: { start: string | Date; end: string | Date }) => {
  if (value && typeof value === 'object' && 'start' in value && 'end' in value) {
    pendingValue.value = {
      start: value.start ? dayjs(value.start).format(emittedFormat.value) : '',
      end: value.end ? dayjs(value.end).format(emittedFormat.value) : ''
    };

    setTimeout(() => {
      datePickerRef.value?.showPopover();
    }, 10);
  }
};

const onValueChange = (value: string) => {
  if (typeof value === 'string') {
    const [startStr, endStr] = value.split(rangeSeparator).map((str) => str.trim());
    pendingValue.value = {
      start: startStr && dayjs(startStr, localisedMask.value).isValid()
        ? dayjs(startStr, localisedMask.value).format(emittedFormat.value)
        : '',
      end: endStr && dayjs(endStr, localisedMask.value).isValid()
        ? dayjs(endStr, localisedMask.value).format(emittedFormat.value)
        : ''
    };
  }
};

const onConfirm = async () => {
  confirmedValue.value = JSON.parse(JSON.stringify(pendingValue.value));
  emitDate(confirmedValue.value);
  await datePickerRef.value?.hidePopover();
};

const onCancel = async () => {
  pendingValue.value = JSON.parse(JSON.stringify(confirmedValue.value));
  await datePickerRef.value?.hidePopover();
};

const emitDate = (emitValue: { start: string; end: string }) => {
  emit('update:modelValue', emitValue);
  emit('change', emitValue);
};

const onValueClear = (event?: Event) => {
  if (event) {
    event.stopPropagation();
    event.preventDefault();
  }

  pendingValue.value = { start: '', end: '' };
  confirmedValue.value = { start: '', end: '' };
  emit('update:modelValue', { start: '', end: '' });
  emit('change', { start: '', end: '' });

  setTimeout(() => {
    datePickerRef.value?.hidePopover();
  }, 50);
};

const showCalendar = async (event?: Event) => {
  event?.stopPropagation();
  event?.preventDefault();

  const datePicker = datePickerRef.value;
  if (datePicker) {
    await datePicker.showPopover();

    setTimeout(() => {
      const popoverEl = document.querySelector('.vc-popover-content-wrapper');
      if (popoverEl) {
        popoverEl.classList.add('vc-no-auto-hide');
      }
    }, 50);
  }
};

const clickAway = async () => {
  const datePicker = datePickerRef.value;
  if (datePicker) {
    await datePicker.hidePopover();
  }
};

onMounted(() => {
  window.addEventListener('wheel', () => clickAway());
  window.addEventListener('resize', () => clickAway());
});

onBeforeUnmount(() => {
  window.removeEventListener('wheel', () => clickAway());
  window.removeEventListener('resize', () => clickAway());
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    (__props.label?.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("label", {
            for: _unref(id),
            class: "inline-block text-base font-medium leading-tight"
          }, _toDisplayString(__props.label), 9, _hoisted_2),
          (__props.isOptional)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(_ctx.$t('platform.common.optional')), 1))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    _createVNode(_unref(DatePicker), {
      ref_key: "datePicker",
      ref: datePicker,
      "model-value": date.value,
      masks: datePickerMasks.value,
      mode: __props.mode,
      is24hr: is24hr.value,
      "min-date": minimumDate.value,
      "max-date": maximumDate.value,
      locale: localeToValidate.value,
      "input-debounce": 500,
      "is-range": true,
      columns: __props.columns,
      popover: popoverOptions.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (setDate($event))),
      onDidSelect: _cache[2] || (_cache[2] = ($event: any) => ($event.preventDefault()))
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(DateRangeInput), {
          "model-value": inputDisplayValue.value,
          placeholder: `${localisedMask.value.toLowerCase()}${rangeSeparator}${localisedMask.value.toLowerCase()}`,
          icon: "calendar",
          "data-dusk": "date-range-input",
          "icon-position": __props.iconPosition,
          "icon-color": __props.iconColor,
          "icon-size": "xl",
          class: "w-full",
          clear: __props.clear,
          readonly: true,
          error: __props.error,
          "success-message": __props.description,
          size: "small",
          onClick: showCalendar,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (onValueChange($event))),
          onClear: onValueClear
        }, null, 8, ["model-value", "placeholder", "icon-position", "icon-color", "clear", "error", "success-message"])
      ]),
      footer: _withCtx(() => [
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_unref(BaseButton), {
            size: "small",
            color: "ghost",
            onClick: onCancel
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('platform.common.close')), 1)
            ]),
            _: 1
          }),
          _createVNode(_unref(BaseButton), {
            size: "small",
            color: "primary",
            "data-dusk": "date-range-confirm",
            onClick: onConfirm
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('platform.common.search')), 1)
            ]),
            _: 1
          })
        ])
      ]),
      _: 1
    }, 8, ["model-value", "masks", "mode", "is24hr", "min-date", "max-date", "locale", "columns", "popover"])
  ]))
}
}

})